import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryPointDetails } from '../types/delivery-point-details.type';
import { ADMIN_DELIVERY_POINTS_API_PATHS } from '../constants/delivery-points-api-paths.constants';
import { sortSchedules } from '../utils/sort-schedules.util';
import { sortClosures } from '../utils/sort-closures.util';

type UseAdminGetDeliveryPointByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  deliveryPoint: DeliveryPointDetails | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetDeliveryPointByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  deliveryPoint: null,
};

const withSortedCalendar = (deliveryPoint: DeliveryPointDetails): DeliveryPointDetails => ({
  ...deliveryPoint,
  schedules: sortSchedules(deliveryPoint.schedules),
  closures: sortClosures(deliveryPoint.closures),
});

export function useAdminGetDeliveryPointById({
  deliveryPointId,
}: {
  deliveryPointId: string;
}): UseAdminGetDeliveryPointByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<DeliveryPointDetails | undefined>(
        ADMIN_DELIVERY_POINTS_API_PATHS.GET_DELIVERY_POINT_BY_ID(deliveryPointId)
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        deliveryPoint: response ? withSortedCalendar(response) : null,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la récupération du point de livraison'
        ),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [deliveryPointId, enqueueSnackbar]);

  useEffect(() => {
    setResult(INITIAL_STATE);
  }, [deliveryPointId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
